import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import { createUrl, request } from 'app/utils';

import { useProducts, ProductEvolutionDetailsApiResponse, ProductsContinuationApiResponse, AvailableClassesApiResponse } from 'features/products';
import { Flex, Section, Typography, Loading, Page, RichContent } from 'components';
import { ProductDetailsApiResponse } from 'features/products/types';
import { LivesApiResponse, useLives } from 'features/lives';
import { TeachersApiResponse, useTeachers } from 'features/teachers';
import { useSchool } from 'features/school';
import { useMenu } from 'features/menu';
import { AgreementsMessage } from 'features/agreements';
import { RecommendationsApiResponse, useRecommendations } from 'features/recommendations';
import { NextItemsArea } from 'app/types/cards/NextItemsArea';
import { useResources } from 'features/resources';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { EventButtonDownload, EventListLives, EventsEnrollmentBox } from 'features/events';
import styled from 'styled-components';
import { Icon } from 'components/icon';
import Comments from 'features/comments';
import { EventTypeEnumMapping } from 'features/events/types';
import { ModulesApiResponse, useModules } from 'features/modules';
import moment from 'moment';
import { useTracking } from 'features/tracking';

interface ProductDetailsProps {
  nextItems?: NextItemsArea;
  idDashboard?: boolean;
  enroll?: boolean;
}

const HeaderOverlay = styled(Flex)`
  width: 100%;
  height: 100%;
  background: url('/images/headerOverlay.png');
  position: absolute;
  background-size: cover;
  top: 0;
  left: 0;
`;

const EventIcon = styled(Icon)`
  width: 24px;
`;

const IconPrimary = styled(Icon)`
  width: 24px;
  color: ${({ theme }) => theme.colors.primary_400};
`;

const ProductDetailsView = ({ nextItems, idDashboard = false, enroll }: ProductDetailsProps): ReactElement => {
  const {
    productDetails,
    availableClasses,
    setProductDetails,
    clearProductDetails,
    productEvolutionDetails,
    setProductEvolutionDetails,
    clearProductEvolutionDetails,
    setProductsContinuation,
    clearProductsContinuation,
    setAvailableClasses,
    clearAvailableClasses,
  } = useProducts();
  const { lives, setLives, clearLives } = useLives();
  const { setTeachers, clearTeachers } = useTeachers();
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [reload, setReload] = useState(false);
  const { apiGatewayUrl } = useSchool();
  const { setModules, clearModules } = useModules();
  const { setRecommendations, clearRecommendations } = useRecommendations();
  const { backPathname, setBackPathname } = useMenu();
  const [searchParams] = useSearchParams();

  const { schoolAlias } = useSchool();
  const {
    schools: { schoolMPC },
  } = useResources();
  const navigate = useNavigate();

  // eslint-disable-next-line
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | false | null>(null);

  const { productSlug, productClassUuid, contentUuid } = useParams();
  const confirmationCode = searchParams.get('code') || '';

  const apiFetch = useCallback(
    async (forceEnroll = false) => {
      setLoadingEdit(true);

      await request({
        url: `/api/v1/School/Products/${productSlug}${forceEnroll || enroll ? `?enroll=true` : ''}${
          (forceEnroll || enroll) && schoolAlias === 'b3' ? `&sendEmail=false` : ''
        }`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(async ({ data: productDetailsData }: AxiosResponse<ProductDetailsApiResponse>) => {
        setProductDetails(productDetailsData);

        await Promise.all([
          productDetailsData?.enrolled
            ? request({
                url: `/api/v1/School/ProductAssessments/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<ProductEvolutionDetailsApiResponse>) => {
                setProductEvolutionDetails(response.data);
              })
            : null,

          request({
            url: `/api/v1/School/ProductContentLives/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
            method: 'get',
            baseURL: apiGatewayUrl,
          }).then((response: AxiosResponse<LivesApiResponse>) => {
            setLives(response.data);
          }),

          request({
            url: `/api/v1/School/ProductContentTeachers/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
            method: 'get',
            baseURL: apiGatewayUrl,
          }).then((response: AxiosResponse<TeachersApiResponse>) => {
            setTeachers(response.data);
          }),

          request({
            url: `/api/v1/School/AvailableClasses/${productSlug}`,
            method: 'get',
            baseURL: apiGatewayUrl,
          }).then((response: AxiosResponse<AvailableClassesApiResponse>) => {
            setAvailableClasses(response.data);
            setReload(true);

            if (response.data?.current?.allowEnrollments) {
              request({
                url: `/api/v1/OfferPayment/Buy/${productClassUuid}`,
                method: 'get',
                baseURL: apiGatewayUrl,
              })
                .then(
                  (
                    response: AxiosResponse<{
                      link: string;
                    }>
                  ) => {
                    setPaymentLinkUrl(response.data.link);
                  }
                )
                .catch(() => {});
            }
          }),
        ])
          .catch(() => {})
          .finally(() => {
            setLoading(false);
            setLoadingEdit(false);
            if (!productClassUuid && !contentUuid) {
              setReload(true);
            }
          });
      });
    },
    [productSlug, productClassUuid]
  );

  function openMap(locationAddress: string) {
    const url = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(locationAddress);
    window.open(url, '_blank');
  }

  useEffect(() => {
    apiFetch();

    return () => {
      clearProductDetails();
      clearModules();
      clearLives();
      clearTeachers();
      clearProductEvolutionDetails();
      clearProductsContinuation();
      clearAvailableClasses();
      clearRecommendations();

      if (!backPathname) {
        setBackPathname(
          productClassUuid ? createUrl('productClassDetails', [productSlug, productClassUuid]) : createUrl('productDetails', [productSlug])
        );
      }
    };
  }, [productSlug, productClassUuid]);

  const { eventTracker } = useTracking();

  useEffect(() => {
    if (productDetails) {
      if (schoolMPC) {
        eventTracker({ category: 'checkout', action: 'Pageview', label: productDetails?.product?.name });
      }
    }
  }, [productDetails, enroll]);

  useEffect(() => {
    if (!productClassUuid && !contentUuid && availableClasses && productDetails) {
      navigate(
        `${createUrl('events/details', [productSlug, availableClasses?.current.productClassUuid, productDetails?.event.commentContentUuid])}${
          confirmationCode ? `/?code=${confirmationCode}` : ''
        }`
      );
    }
  }, [reload]);

  if (loading) return <Loading label="Por favor, aguarde." />;

  const startDate = formatDateFromUtcToTimezone(productDetails?.event?.startDate).format('YYYY-MM-DD');
  const finalDate = formatDateFromUtcToTimezone(productDetails?.event?.endDate).format('YYYY-MM-DD');
  const iquallDate = moment(startDate).isSame(finalDate);

  return productDetails ? (
    <Page title={productDetails.product.name}>
      <AgreementsMessage productSlug={productSlug} />

      {loadingEdit && <Loading alpha />}

      <Flex flexGrow={0} flexDirection="column">
        <Flex
          flexGrow={0}
          minHeight={{ m: 240, t: 360, d: 500 }}
          bg={schoolAlias === 'b3' ? 'dark_350' : 'primary_400'}
          backgroundImage={productDetails.product.coverImageUrl ? `url(${productDetails.product.coverImageUrl})` : undefined}
          backgroundSize="cover"
          position="relative"
        >
          <HeaderOverlay alignItems="center">
            <Section position="relative" alignItems="center" justifyContent="space-around">
              <Flex position="relative" flexGrow={0} flexDirection="column" maxWidth={{ d: 680 }} marginRight={100}>
                <Flex alignItems="center">
                  <EventIcon icon="radiotower" />
                  <Typography type="type2" fontWeight="bold" color="white" upper ml={2}>
                    {productDetails.product.hat}
                  </Typography>
                </Flex>

                <Typography as="h1" type="type9" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                  {productDetails.product.name}
                </Typography>
                {iquallDate ? (
                  <>
                    <Typography type="type3" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                      {formatDateFromUtcToTimezone(productDetails?.event?.startDate).format('D [de] MMMM')}
                    </Typography>
                    <Typography type="type2" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                      {formatDateFromUtcToTimezone(productDetails?.event?.startDate).format('HH:mm').toLowerCase()} às{' '}
                      {formatDateFromUtcToTimezone(productDetails?.event?.endDate).format('HH:mm').toLowerCase()}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography type="type3" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                      De {formatDateFromUtcToTimezone(productDetails?.event?.startDate).format('D [de] MMMM HH:mm')}
                    </Typography>
                    <Typography type="type3" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                      à {formatDateFromUtcToTimezone(productDetails?.event?.endDate).format('D [de] MMMM HH:mm')}
                    </Typography>
                  </>
                )}

                <Typography type="type0" color="white" upper mt={{ m: '10px', t: 15 }}>
                  {EventTypeEnumMapping[productDetails?.event?.eventType]}
                </Typography>

                <Typography type="type1" fontWeight="bold" color="white" mt={{ m: '10px', t: 15 }}>
                  {productDetails?.event?.headlineText}
                </Typography>
              </Flex>

              <EventsEnrollmentBox paymentLinkUrl={paymentLinkUrl} apiFetch={apiFetch} />
            </Section>
          </HeaderOverlay>
        </Flex>

        <Flex flexGrow={0} bg="gray_300">
          <Section py={{ m: 40, t: 60 }}>
            <Flex flexWrap={{ m: 'wrap', d: 'nowrap' }}>
              <Flex mr={{ m: 0, d: 60 }} flexGrow={1} flexDirection="row">
                <Flex flexGrow={0} flexDirection="column">
                  <Typography as="h1" type="type9" fontWeight="bold" mb="5px">
                    Sobre o Evento
                  </Typography>
                  <RichContent>{productDetails?.product?.description}</RichContent>
                </Flex>
              </Flex>

              <Flex flexGrow={0} maxWidth={400} minWidth={400}>
                {productDetails?.event?.locationAddress && (
                  <Flex bg="white" p={4} flexDirection="column" flexGrow={0} minWidth={400}>
                    <Flex flexDirection="row" alignItems="center" flexGrow={0} mb={4}>
                      <IconPrimary icon="map" />
                      <Typography type="type2" color="primary_400" fontWeight="bold" ml={2} upper>
                        Local
                      </Typography>
                    </Flex>

                    <Typography type="type2" fontWeight="bold" upper mb={2}>
                      {productDetails?.event?.locationName}
                    </Typography>

                    <Typography type="type1" mb={4} color="gray_600">
                      {productDetails?.event?.locationAddress}
                    </Typography>

                    <img width="352" height="142" src={productDetails?.event?.locationMapImage} />

                    <Flex justifyContent="flex-end" flexGrow={0}>
                      <Link to="/" onClick={() => openMap(productDetails?.event?.locationAddress)}>
                        <Typography type="type0" color="primary_400">
                          Abrir mapa no Google Maps
                        </Typography>
                      </Link>
                    </Flex>
                  </Flex>
                )}

                {productEvolutionDetails?.certificateAddress || !!productDetails?.downloads?.length ? (
                  <Flex bg="gray_100" p={4} flexGrow={0} minWidth={400}>
                    {productEvolutionDetails?.certificateAddress && (
                      <Flex mt={3} mb={3} flexDirection="column">
                        <Flex flexDirection="row" alignItems="center" flexGrow={0}>
                          <IconPrimary icon="star" />
                          <Typography type="type2" color="primary_400" fontWeight="bold" ml={2}>
                            Certificado
                          </Typography>
                        </Flex>
                        <EventButtonDownload name="Certificado de conclusão" icon="star" filePath={productEvolutionDetails?.certificateAddress} />
                      </Flex>
                    )}

                    {!!productDetails?.downloads?.length && (
                      <Flex flexDirection="column">
                        <Flex flexDirection="row" alignItems="center" flexGrow={0}>
                          <IconPrimary icon="library" />
                          <Typography type="type2" color="primary_400" fontWeight="bold" ml={2}>
                            Material de apoio
                          </Typography>
                        </Flex>
                        <Flex mt={3}>
                          {productDetails?.downloads.map((item, index) => {
                            return (
                              <EventButtonDownload
                                type={item.downloadType}
                                filePath={item.downloadUrl}
                                name={item.name}
                                fileName={item.downloadFileName}
                                key={item.name}
                              />
                            );
                          })}
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          </Section>
        </Flex>

        {Boolean(lives && lives.length) && (
          <Flex flexGrow={1} my={{ m: 40, t: 60 }}>
            <EventListLives lives={lives} enrolled={productDetails?.enrolled} productClassUuid={productClassUuid} />
          </Flex>
        )}

        {productDetails?.enrolled && <Comments />}
      </Flex>
    </Page>
  ) : (
    <Loading label="Por favor, aguarde." />
  );
};
export default ProductDetailsView;
